import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Stack,
  Grid,
  MenuItem,
  Paper,
} from "@mui/material";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useForm, SubmitHandler } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import MpgInput from "../../../../CommonFormComp/MpgInput";
import MpgCheckbox from "../../../../CommonFormComp/MpgCheckbox";
import moment from "moment-timezone";
import MpgItem from "../../../../CommonFormComp/MpgItem";

import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import {
  useGetTimezonesListQuery,
} from "../../../../reactQueries/commonRQHooks.js";
import { useUpdateAdminMessageByIdMutation } from "../../../../reactQueries/AppApi/rqHooksForAdminMessages";
import { adminMsgViewPath } from "../../../../routes/routePaths";
import MpgErrorDialog from "../../../../common/dialog/MpgErrorDialog";
import MpgLoadingDialog from "../../../../common/dialog/MpgLoadingDialog.jsx";
import MpgAutocompleteSingle from "../../../../CommonFormComp/MpgAutocompleteSingle.jsx";
import { getAccessTypes } from "../../../../hooksAndFunctions/functions/getAccessTypes.js";
import { useGetSiteCoreCMSData } from "../../../../reactQueries/commonRQHooks.js";

const EditAdminMessage = ({ adminMessageDataById }) => {

  const {
    clientPortalAdminType
  } = getAccessTypes()
  const {
    isClientPortalAdmin,
    hasA4Access
  } = clientPortalAdminType

  const userName = JSON.parse(window.sessionStorage.getItem("UserAccount"))?.[
    "name"
  ];

  const navigate = useNavigate();

  const {
    mutate: updateAdminMessage,
    isError: isErrorOnUpdate,
    isLoading: isLoadingOnUpdate,
    isSuccess: isSuccessOnUpdate,
    data: dataOnUpdate,
    error: errorOnUpdate,
  } = useUpdateAdminMessageByIdMutation();

    // get cms content
    const {
      isSuccess: isSuccessOnSiteCoreCMS,
      error: errorOnSiteCoreCMS,
      data: adminMessageCMSDataFromAPI,
      isError: isErrorOnSiteCoreCMS,
      isFetching: isFetchingOnSiteCoreCMS
    } = useGetSiteCoreCMSData({ pagename: "AdminMessages" });
  
    const [adminMessagesCMSData, setAdminMessagesCMSData] = useState({ title: "", description: "", tabNavigationTitle: "", pageContent: {} });
  
    const fetchAdminMessageCMSData = () => {
      if (adminMessageCMSDataFromAPI && adminMessageCMSDataFromAPI?.Pages) {
        adminMessageCMSDataFromAPI.Pages.map((el) => {
          if (el.Key === "AdminMessages") {
            const { Title, Description, TabNavigationTitle, Components } = el;
            setAdminMessagesCMSData({ title: Title, description: Description, tabNavigationTitle: TabNavigationTitle, pageContent: Components });
          }
        });
      }
    }
  
    useEffect(() => {
      fetchAdminMessageCMSData();
    }, [adminMessageCMSDataFromAPI]);
  
    const returnLabel = (labelArray=[], key) => {
        return labelArray.find((x) => x.Key === key)?.Phrase || '';
    }
  
    const fetchAdminMessageDetailsLabel = (key) => {
      if (adminMessagesCMSData && adminMessagesCMSData?.pageContent["AdminMessageDetailsComponent"]) {
        const adminMessagesCMSDataLabel = adminMessagesCMSData.pageContent["AdminMessageDetailsComponent"];
        return returnLabel(adminMessagesCMSDataLabel?.Labels, key)
      }
      return '';
    }
  


  const schema = yup.object().shape({
    message_header: yup.string().label('This field').required(fetchAdminMessageDetailsLabel('AdminMessageDetails.ValidationMsgRequiredField')).max(200),
    content: yup.string(fetchAdminMessageDetailsLabel('AdminMessageDetails.ValidationMsgRequiredField')).required().label('This field').max(1300),
    persistent: yup.boolean().label('This field').default(false).optional(),
    timezone: yup.object().label('This field').required(fetchAdminMessageDetailsLabel('AdminMessageDetails.ValidationMsgRequiredField')).typeError("This field is a required"),
    fromDate: yup
      .date()
      .label('This field')
      .required()
      .typeError(fetchAdminMessageDetailsLabel('AdminMessageDetails.ValidationMsgRequiredField')),
    toDate: yup
      .date()
      .label('This field')
      .required()
      .test(
        "from-less-than-to",
        fetchAdminMessageDetailsLabel('AdminMessageDetails.ValidationMsgRequiredField'),
        function (value) {
          const newTd = moment(value).toISOString();
          const newFd = moment(this.parent.fromDate).toISOString();

          if (newTd > newFd) {
            return true;
          } else {
            return false;
          }
        }
      )
      .typeError(fetchAdminMessageDetailsLabel('AdminMessageDetails.ValidationMsgRequiredField')),
  });
  const {
    isSuccess,
    isFetching,
    error,
    data: timezonesList,
  } = useGetTimezonesListQuery();
  /** !!!!!!!! autocomplete option lists !!!!!!! */
  const timezonesAutocompleteList = !!timezonesList && timezonesList.map((item) => {
    return {
      id: item.Id,
      selectOptionLabel: item.MasterTimeZoneName,
      selectOptionValue: item.Id,
      extraData: item
    }
  })

  const defaultValues = {
    message_header: adminMessageDataById?.MessageHeader || "",
    content: adminMessageDataById?.MessageContent || "",
    persistent: adminMessageDataById?.Persistent,
    /**converting dates to moment object */
    fromDate: moment(adminMessageDataById?.FromDate) || null,
    toDate: moment(adminMessageDataById?.ToDate) || null,
  };
  const rhfFormInstance = useForm({
    defaultValues,
    resolver: yupResolver(schema),
  });
  const {
    setValue,
    getValues,
    formState: { errors },
    watch
  } = rhfFormInstance;

  const selectedTimezoneDetails = watch('timezone')
  const selectedIANATimeZoneId = selectedTimezoneDetails?.extraData?.IANATimeZoneId

  /**populate exisitng value for timezone dropdown */
  useEffect(() => {
    if (!!adminMessageDataById?.TimeZoneDetails) {
      const timeZoneDetails = adminMessageDataById?.TimeZoneDetails;

      const existingTimeZoneDetails = {
        id: timeZoneDetails.Id,
        selectOptionLabel: timeZoneDetails.MasterTimeZoneName,
        selectOptionValue: timeZoneDetails.Id,
        extraData: adminMessageDataById?.TimeZoneDetails
      }

      setValue("timezone", existingTimeZoneDetails || "");
    }
  }, [adminMessageDataById?.TimeZoneDetails]);

  const onSubmit = (data) => {
    /**we r using timezones so need to convert local time into UTC format to store in DB */
    const toDate_inUTC = moment(data?.toDate).utc().toISOString()
    const fromDate_inUTC = moment(data?.fromDate).utc().toISOString()

    const body = {
      Id: adminMessageDataById?.Id,
      MessageHeader: data?.message_header || "",
      MessageContent: data?.content || "",
      Language: adminMessageDataById?.Language || "",
      LanguageId: adminMessageDataById?.LanguageId || "",
      Persistent: data?.persistent,
      TimeZoneId: data?.timezone?.id || "",
      TimeZone: data?.timezone?.extraData?.MasterTimeZoneName || "",
      IsActive: true,
      FromDate: fromDate_inUTC,
      ToDate: toDate_inUTC,
      UpdatedBy: userName || "",
    };

    updateAdminMessage({ body, Id: adminMessageDataById?.Id });
  };

  useEffect(() => {
    if (!!isSuccessOnUpdate) {
      navigate(adminMsgViewPath);
    }
  }, [isSuccessOnUpdate]);

  return (
    <Box sx={{ width: "100%" }}>
      <Typography variant="h5" align="center" sx={{ fontWeight: "bolder" }}>
        {fetchAdminMessageDetailsLabel('AdminMessageDetails.UpdateAdminMessage')}
      </Typography>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems="center"
        spacing={1}
        m={1}
      >
        <Button
          variant="outlined"
          size="small"
          component={Link}
          to={adminMsgViewPath}
        >
          {fetchAdminMessageDetailsLabel('AdminMessageDetails.AdminMessage')}
        </Button>
      </Stack>

      <Box
        component="form"
        noValidate
        autoComplete="off"
        onSubmit={rhfFormInstance.handleSubmit(onSubmit)}
      >
        <Grid container spacing={1}>
          <Grid item xs={12} sm={6}>
            <MpgItem sx={{ padding: { xs: "20px" } }}>
              <Typography
                variant="h6"
                align="center"
                sx={{ fontWeight: "bolder" }}
              >
                {fetchAdminMessageDetailsLabel('AdminMessageDetails.MessageDetails')}
              </Typography>
              <MpgInput
                rhfFormInstance={rhfFormInstance}
                name={"message_header"}
                label={fetchAdminMessageDetailsLabel('AdminMessageDetails.MessageHeader')}
                fullWidth
              />

              <MpgInput
                rhfFormInstance={rhfFormInstance}
                name={"content"}
                label={fetchAdminMessageDetailsLabel('AdminMessageDetails.MessageContent')}
                placeholder={fetchAdminMessageDetailsLabel('AdminMessageDetails.AddMessageContentHere')}
                multiline
                rows={5}
                fullWidth
              />
              <div style={{ textAlign: "left" }}>
                <MpgCheckbox
                  rhfFormInstance={rhfFormInstance}
                  name={"persistent"}
                  label={fetchAdminMessageDetailsLabel('AdminMessageDetails.Persistent')}
                  fullWidth
                  sx={{ textAlign: "left" }}
                  defaultChecked={adminMessageDataById?.Persistent}
                />
              </div>
              <MpgAutocompleteSingle
                rhfFormInstance={rhfFormInstance}
                name={'timezone'}
                label={fetchAdminMessageDetailsLabel('AdminMessageDetails.Timezones')}
                fullWidth
                optionList={timezonesAutocompleteList || []}
              />

              <Stack
                direction={{ sm: "column", md: "row" }}
                justifyContent="space-between"
                alignItems="center"
                spacing={1}
                mt={2}
              >

                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DateTimePicker
                    name="fromDate"
                    label={fetchAdminMessageDetailsLabel('AdminMessageDetails.From')}
                    disablePast
                    value={getValues("fromDate")}
                    onChange={(newValue) => {
                      setValue("fromDate", newValue, {
                        shouldValidate: true,
                      });
                    }}
                    timezone={!!selectedIANATimeZoneId ? selectedIANATimeZoneId : ''}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!errors["fromDate"],
                        helperText: errors["fromDate"]?.message,
                      },
                    }}
                  />
                </LocalizationProvider>

                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DateTimePicker
                    disablePast
                    name="toDate"
                    label={fetchAdminMessageDetailsLabel('AdminMessageDetails.To')}
                    value={getValues("toDate")}
                    onChange={(newValue) => {
                      setValue("toDate", newValue, { shouldValidate: true });
                    }}
                    timezone={!!selectedIANATimeZoneId ? selectedIANATimeZoneId : ''}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!errors["toDate"],
                        helperText: errors["toDate"]?.message,
                      },
                    }}
                  />
                </LocalizationProvider>
              </Stack>
            </MpgItem>
          </Grid>
          <Grid item xs={12} sm={6}>
            <MpgItem>
              <Typography
                variant="h6"
                align="center"
                sx={{ fontWeight: "bolder" }}
              >
                {fetchAdminMessageDetailsLabel('AdminMessageDetails.Languages')}
              </Typography>
              <div style={{ textAlign: "left" }}>
                <MpgCheckbox
                  rhfFormInstance={rhfFormInstance}
                  name={`${adminMessageDataById?.Language}-lang`}
                  label={`${adminMessageDataById?.Language}`}
                  fullWidth
                  sx={{ textAlign: "left" }}
                  defaultChecked={true}
                  disabled={true}
                />
              </div>
            </MpgItem>
          </Grid>
        </Grid>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent={"space-evenly"}
          mt={3}
        >
          <Button
            variant="outlined"
            type="submit"
            size="small"
            disabled={!!hasA4Access}
          >
            {fetchAdminMessageDetailsLabel('AdminMessageDetails.UpdateMessage')}
          </Button>
        </Stack>
      </Box>

      {/** error dialog on update action */}
      <MpgErrorDialog
        open={!!isErrorOnUpdate}
      />
      <MpgLoadingDialog
        open={!!isLoadingOnUpdate}
      />
    </Box>
  );
};

export default EditAdminMessage;
